import useAsync from 'hooks/useAsync';
import { useLocation } from 'react-router-dom';
import { Assessment } from 'types/api/envScales/Assessment';
import { initialValue, getStatusByPage, isSummaryEnabled } from './utils';
import { getObservationValidations } from 'actions/envScales/observations';
import { ObservationValidations } from 'types/api/envScales/ObservationValidations';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode
} from 'react';

const ValidationsContext = createContext(initialValue);
ValidationsContext.displayName = 'ValidationsContext';

interface ValidationsProviderProps {
  /** Children component to render. */
  children: ReactNode;

  /** Assessment data. */
  assessment: Assessment;
}

export function ValidationsProvider({
  children,
  assessment
}: ValidationsProviderProps) {
  const { pathname } = useLocation();
  const observationId = assessment.observation!.id;
  const [showErrorStatus, setShowErrorStatus] = useState(false);

  const {
    run,
    data: validations,
    setData: setValidations,
    isLoading
  } = useAsync<ObservationValidations>();

  const [resolvedPaths, setResolvedPaths] = useState<Record<string, boolean>>(
    {}
  );

  useEffect(() => {
    setResolvedPaths({});
  }, [validations]);

  const reportFieldResolution = useCallback(
    (path: string, isResolved: boolean) => {
      setResolvedPaths(previous => {
        if (previous[path] === isResolved) {
          return previous;
        }

        return { ...previous, [path]: isResolved };
      });
    },
    []
  );

  const previousPathnameRef = useRef(pathname);
  const [pendingPathname, setPendingPathname] = useState<string | null>(null);

  useEffect(() => {
    if (previousPathnameRef.current !== pathname) {
      setPendingPathname(previousPathnameRef.current);
      previousPathnameRef.current = pathname;
    }

    run(getObservationValidations(observationId));
  }, [run, pathname, observationId]);

  useEffect(() => {
    if (!isLoading) {
      setPendingPathname(null);
    }
  }, [isLoading]);

  const errors = validations?.errors || {};
  const statusByPage = getStatusByPage(
    validations,
    assessment,
    showErrorStatus,
    resolvedPaths
  );

  async function validate() {
    const { data } = await getObservationValidations(observationId);
    setValidations(data);
    setShowErrorStatus(true);
    return isSummaryEnabled(data, assessment);
  }

  const value = {
    status: statusByPage,
    validate,
    errors,
    sections: validations?.sections,
    showErrorStatus,
    resolvedPaths,
    reportFieldResolution,
    pendingPathname
  };

  return (
    <ValidationsContext.Provider value={value}>
      {children}
    </ValidationsContext.Provider>
  );
}

export function useValidations() {
  const context = useContext(ValidationsContext);

  if (!context) {
    throw new Error(
      'useValidations must be used within an <ValidationsProvider />'
    );
  }

  return context;
}
